/**
 * Shared presentation of an embedded document preview.
 *
 * Every surface that embeds a generated document — the templates workspace, the
 * beneficiary declaration drawer, the composer's generated-attachment drawer —
 * must look like the same viewer, and each had its own copy of this literal. It
 * is the sort of value that drifts silently: a frame taller than its page reads
 * as a different (worse) component rather than as a bug, so nothing forces the
 * copies back into step.
 *
 * The companion `PDF_VIEWER_PARAMS` — open-parameters that stripped the
 * browser's native viewer chrome — was removed with VALE-891. Previews no longer
 * hand the document to the browser's PDF plugin at all: `DocumentPreview`
 * (`@/components/ui/document-preview`) renders the pages itself, so there is no
 * viewer chrome left to configure and nothing for a URL fragment to address.
 */

/**
 * A single embedded page drawn by the host itself: A4 proportions (1:√2) rather
 * than a fixed viewport height, so the frame matches the document instead of
 * leaving a backdrop showing above and below it. Capped and centred on a
 * neutral mat.
 *
 * **This is for content the host paints on a page — an image.** A PDF handed to
 * `DocumentPreview` no longer wants it: pdf.js draws each of the document's own
 * pages as its own sheet, so wrapping that in a second sheet nests one page
 * inside another. Use `PDF_VIEWPORT_CLASS` for those.
 */
export const PDF_PAGE_CLASS =
  'mx-auto block aspect-[1/1.41] w-full max-w-[720px] rounded-sm border border-border bg-card shadow-sm';

/**
 * The scroll viewport a multi-page PDF is read in — the same geometry as
 * `PDF_PAGE_CLASS` and deliberately none of its chrome.
 *
 * The distinction is what makes page breaks visible. `DocumentPreview` used to
 * paint every page of a document onto one continuous canvas stack, so the sheet
 * treatment sensibly lived here, on the container. Now that pdf.js lays the
 * document out itself, each page carries its own border, shadow and white
 * ground, and the container's job is the opposite one: be the mat those sheets
 * sit on, so the gap between page 1 and page 2 reads as a gap rather than as
 * more of the same document. Keeping `bg-card` here would fill that gap with
 * page-coloured white and put a border around the whole stack — exactly the
 * "one long document" effect the split exists to remove.
 *
 * The A4 aspect stays because it is doing a second, unrelated job: giving the
 * viewport a height. A single-page document therefore still occupies the same
 * page-shaped area it always did.
 */
export const PDF_VIEWPORT_CLASS =
  'mx-auto block aspect-[1/1.41] w-full max-w-[720px]';

/**
 * Stamp a refresh nonce onto a preview URL so a host can force a re-read.
 *
 * `DocumentPreview` refetches when — and only when — its `src` changes, which is
 * the right rule for a URL that names a file and the wrong one for the panes
 * whose URL is stable while the bytes behind it are not: the estate account
 * re-renders from the matter's current figures, and a declaration's document is
 * rewritten in place by a Word save-back. Those panes hold a nonce their host
 * bumps, and this is how the nonce reaches the fetch.
 *
 * A query parameter rather than a fragment, because a fragment never reaches the
 * server and would not miss the cache either. The name is `t` to match the one
 * the letter preview has always used.
 */
export function withPreviewRefresh(
  src: string,
  refreshNonce: number | undefined
): string {
  if (refreshNonce === undefined) return src;
  return `${src}${src.includes('?') ? '&' : '?'}t=${refreshNonce}`;
}
